import { message } from "antd";
import useRequest from "./useRequest";
import { useState } from "react";

const useCalendarTasks = () => {
  const [tasks, setTasks] = useState({ results: [], count: 0 });
  const [task, setTask] = useState(null);
  const [stats, setStats] = useState(null);
  const [pendingReminders, setPendingReminders] = useState({ count: 0, results: [] });
  const [recurringTemplates, setRecurringTemplates] = useState({ results: [], count: 0 });
  const [recurringTemplate, setRecurringTemplate] = useState(null);
  const [loading, setLoading] = useState({
    get: false,
    post: false,
    put: false,
    delete: false,
  });

  const { request } = useRequest();

  // Obtener lista de tareas con filtros
  async function getTasks(params = {}) {
    setLoading((prev) => ({ ...prev, get: true }));
    const data = await request(
      "GET",
      "tasks/",
      (data) => {
        setTasks(data);
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {},
      params
    );
    return data;
  }

  // Obtener detalle de una tarea
  async function getTask(taskId) {
    setLoading((prev) => ({ ...prev, get: true }));
    const data = await request(
      "GET",
      `tasks/${taskId}/`,
      (data) => {
        setTask(data);
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {}
    );
    return data;
  }

  // Crear tarea
  async function createTask(body) {
    setLoading((prev) => ({ ...prev, post: true }));
    const data = await request(
      "POST",
      "tasks/",
      (data) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.success("Tarea creada exitosamente");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.error("Error al crear la tarea");
        return error;
      },
      body
    );
    return data;
  }

  // Actualizar tarea
  async function updateTask(taskId, body) {
    setLoading((prev) => ({ ...prev, put: true }));
    const data = await request(
      "PATCH",
      `tasks/${taskId}/`,
      (data) => {
        setLoading((prev) => ({ ...prev, put: false }));
        message.success("Tarea actualizada exitosamente");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, put: false }));
        message.error("Error al actualizar la tarea");
        return error;
      },
      body
    );
    return data;
  }

  // Archivar tarea (soft delete)
  async function archiveTask(taskId) {
    setLoading((prev) => ({ ...prev, delete: true }));
    const data = await request(
      "POST",
      `tasks/${taskId}/archive/`,
      (data) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.success("Tarea archivada exitosamente");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.error("Error al archivar la tarea");
        return error;
      },
      {}
    );
    return data;
  }

  // Restaurar tarea
  async function restoreTask(taskId) {
    setLoading((prev) => ({ ...prev, put: true }));
    const data = await request(
      "POST",
      `tasks/${taskId}/restore/`,
      (data) => {
        setLoading((prev) => ({ ...prev, put: false }));
        message.success("Tarea restaurada exitosamente");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, put: false }));
        message.error("Error al restaurar la tarea");
        return error;
      },
      {}
    );
    return data;
  }

  // Obtener historial de una tarea
  async function getTaskHistory(taskId) {
    const data = await request(
      "GET",
      `tasks/${taskId}/history/`,
      (data) => data,
      (error) => error,
      {}
    );
    return data;
  }

  // Agregar comentario
  async function addComment(taskId, content) {
    setLoading((prev) => ({ ...prev, post: true }));
    const data = await request(
      "POST",
      `tasks/${taskId}/comments/`,
      (data) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.success("Comentario agregado");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.error("Error al agregar comentario");
        return error;
      },
      { content }
    );
    return data;
  }

  // Eliminar comentario
  async function deleteComment(commentId) {
    setLoading((prev) => ({ ...prev, delete: true }));
    const data = await request(
      "DELETE",
      `tasks/comments/${commentId}/`,
      (data) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.success("Comentario eliminado");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.error("Error al eliminar comentario");
        return error;
      },
      {}
    );
    return data;
  }

  // Obtener recordatorios pendientes
  async function getPendingReminders() {
    const data = await request(
      "GET",
      "tasks/pending-reminders/",
      (data) => {
        setPendingReminders(data);
        return data;
      },
      (error) => error,
      {}
    );
    return data;
  }

  // Obtener estadisticas
  async function getStats() {
    const data = await request(
      "GET",
      "tasks/stats/",
      (data) => {
        setStats(data);
        return data;
      },
      (error) => error,
      {}
    );
    return data;
  }

  // === TAREAS PERIODICAS ===

  // Listar plantillas de tareas periodicas
  async function getRecurringTemplates(params = {}) {
    setLoading((prev) => ({ ...prev, get: true }));
    const data = await request(
      "GET",
      "tasks/recurring/",
      (data) => {
        setRecurringTemplates(data);
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {},
      params
    );
    return data;
  }

  // Obtener detalle de plantilla periodica
  async function getRecurringTemplate(templateId) {
    setLoading((prev) => ({ ...prev, get: true }));
    const data = await request(
      "GET",
      `tasks/recurring/${templateId}/`,
      (data) => {
        setRecurringTemplate(data);
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {}
    );
    return data;
  }

  // Crear plantilla periodica (genera instancias automaticamente)
  async function createRecurringTemplate(body) {
    setLoading((prev) => ({ ...prev, post: true }));
    const data = await request(
      "POST",
      "tasks/recurring/",
      (data) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.success(`Tarea periodica creada con ${data.instances?.length || 0} instancias`);
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.error("Error al crear la tarea periodica");
        return error;
      },
      body
    );
    return data;
  }

  // Actualizar plantilla periodica
  async function updateRecurringTemplate(templateId, body) {
    setLoading((prev) => ({ ...prev, put: true }));
    const data = await request(
      "PATCH",
      `tasks/recurring/${templateId}/`,
      (data) => {
        setLoading((prev) => ({ ...prev, put: false }));
        message.success("Plantilla actualizada exitosamente");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, put: false }));
        message.error("Error al actualizar la plantilla");
        return error;
      },
      body
    );
    return data;
  }

  // Desactivar plantilla (soft delete)
  async function deleteRecurringTemplate(templateId) {
    setLoading((prev) => ({ ...prev, delete: true }));
    const data = await request(
      "DELETE",
      `tasks/recurring/${templateId}/`,
      (data) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.success("Plantilla desactivada y tareas archivadas");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.error("Error al desactivar la plantilla");
        return error;
      },
      {}
    );
    return data;
  }

  // Regenerar instancias futuras
  async function regenerateRecurringInstances(templateId) {
    setLoading((prev) => ({ ...prev, post: true }));
    const data = await request(
      "POST",
      `tasks/recurring/${templateId}/regenerate/`,
      (data) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.success(data.message);
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.error("Error al regenerar instancias");
        return error;
      },
      {}
    );
    return data;
  }

  // Archivar instancias futuras
  async function deleteRecurringInstances(templateId) {
    setLoading((prev) => ({ ...prev, delete: true }));
    const data = await request(
      "POST",
      `tasks/recurring/${templateId}/delete-instances/`,
      (data) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.success(data.message);
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.error("Error al archivar instancias");
        return error;
      },
      {}
    );
    return data;
  }

  return {
    tasks,
    task,
    stats,
    pendingReminders,
    recurringTemplates,
    recurringTemplate,
    loading,
    setTask,
    setTasks,
    getTasks,
    getTask,
    createTask,
    updateTask,
    archiveTask,
    restoreTask,
    getTaskHistory,
    addComment,
    deleteComment,
    getPendingReminders,
    getStats,
    // Tareas periodicas
    getRecurringTemplates,
    getRecurringTemplate,
    createRecurringTemplate,
    updateRecurringTemplate,
    deleteRecurringTemplate,
    regenerateRecurringInstances,
    deleteRecurringInstances,
  };
};

export default useCalendarTasks;
